import { useState } from 'react'
import type { FormEvent, ReactElement } from 'react'
import { Link } from 'react-router'
import { useT } from '../../i18n/useI18n'
import { legalPages } from '../../data/legal/legalPages'
import { ACCOUNT_PATHS } from '../../account/accountPaths'
import { EMAIL_MAX_LENGTH, NAME_MAX_LENGTH, PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH, registerAccount, resendVerification } from '../../account/accountApi'
import type { RegistrationInput } from '../../account/accountApi'
import { useAccountRequest } from '../../account/useAccountRequest'
import AccountLayout from './AccountLayout'
import { AccountError, AccountNotice, SubmitButton, TextField } from './AccountParts'

const CONTACT_PATH: string = '/contact'
const EMPTY_FORM: RegistrationInput = { email: '', password: '', name: '', companyName: '' }

function RegistrationSent({ email }: { email: string }): ReactElement {
  const t = useT()
  const { state, run } = useAccountRequest()
  return (
    <>
      <AccountNotice title={t('account.register.sentTitle')}>
        <p>{t('account.register.sentBody', { email })}</p>
      </AccountNotice>
      <p className="ac-small">{t('account.register.spam')}</p>
      {state.status === 'done' ? <p className="ac-small ac-strong">{t('account.register.resent')}</p> : (
        <button type="button" className="btn btn-outline-primary" disabled={state.status === 'sending'} onClick={() => void run(() => resendVerification(email))}>{t('account.register.resend')}</button>
      )}
      {state.status === 'failed' && <AccountError error={state.error} />}
      <p className="ac-links"><Link to={ACCOUNT_PATHS.forgotPassword}>{t('account.register.forgot')}</Link></p>
    </>
  )
}

function TermsNote(): ReactElement {
  const t = useT()
  return (
    <p className="ac-small">
      {t('account.register.termsLead')} <Link to={legalPages.terms.path}>{t('account.register.termsLink')}</Link> {t('account.register.privacyLead')} <Link to={legalPages.privacy.path}>{t('account.register.privacyLink')}</Link>.
    </p>
  )
}

export default function AccountRegister(): ReactElement {
  const t = useT()
  const [form, setForm] = useState<RegistrationInput>(EMPTY_FORM)
  const { state, run } = useAccountRequest()
  if (state.status === 'done') return <AccountLayout title={t('account.register.title')}><RegistrationSent email={form.email.trim()} /></AccountLayout>

  const update = (field: keyof RegistrationInput): ((value: string) => void) => (value: string): void => setForm((previous: RegistrationInput) => ({ ...previous, [field]: value }))
  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault()
    void run(() => registerAccount(form))
  }

  return (
    <AccountLayout title={t('account.register.title')} lead={t('account.register.lead')}>
      <form className="ac-form" onSubmit={submit}>
        <TextField label={t('account.email')} value={form.email} onChange={update('email')} type="email" autoComplete="email" required maxLength={EMAIL_MAX_LENGTH} />
        <TextField label={t('account.register.name')} value={form.name} onChange={update('name')} type="text" autoComplete="name" required={false} maxLength={NAME_MAX_LENGTH} />
        <TextField label={t('account.register.company')} value={form.companyName} onChange={update('companyName')} type="text" autoComplete="organization" required={false} maxLength={NAME_MAX_LENGTH} />
        <TextField label={t('account.register.password')} value={form.password} onChange={update('password')} type="password" autoComplete="new-password" required minLength={PASSWORD_MIN_LENGTH} maxLength={PASSWORD_MAX_LENGTH} hint={t('account.register.passwordHint', { min: PASSWORD_MIN_LENGTH })} />
        <TermsNote />
        {state.status === 'failed' && <AccountError error={state.error} />}
        <SubmitButton label={t('account.register.submit')} sending={state.status === 'sending'} />
      </form>
      <p className="ac-links"><Link to={ACCOUNT_PATHS.forgotPassword}>{t('account.register.forgot')}</Link></p>
      <p className="ac-small ac-aside">{t('account.register.accessNote')} <Link to={CONTACT_PATH}>{t('account.register.accessContact')}</Link></p>
    </AccountLayout>
  )
}
